/**
 * SummaryHero — the range-dashboard summary card.
 *
 * Three endpoint cards (health / biodiversity / economic) with optional
 * Floor↔Ceiling whiskers, plus the fixed generation anchor (the physical
 * driver, which never gets a sensitivity band) and the fossil-displacement
 * fuel bar. Purely presentational: the orchestrator prepares the view model.
 *
 * The crediting logic is explained ONCE, for the whole project, in the
 * `CreditingPanel` below the metrics — a single plain-language ladder
 * (simple claim → −fossil that doesn't back down → −credit that would cross
 * grid boundaries → what we count). The per-metric range bars stay; the
 * *why* is consolidated, so users read it in one place, in lay terms.
 */

import { useState } from "react";

export interface WhiskerVM {
  floorText: string;
  ceilingText: string;
  /** 0–100 position of the central marker within the floor→ceiling span. */
  centralPct: number;
}

/** One neighbouring grid's measured cross-border realization, for the copy. */
export interface CreditNeighborVM {
  code: string;
  delta: number; // 0–1 realized fraction
  kind: string; // "measured" | "reasoned" | "reasoned-default"
  gwh?: number | null;
}

/**
 * The one project-level crediting explanation. Percentages of the simple
 * "1-for-1" claim. The four bar segments sum to 100:
 *   floorPct (domestic credit) + crossBorderCreditPct (cross-border credit)
 *   + fossilNoResponsePct (FRF cut) + crossBorderDeclinedPct (un-confirmed).
 */
export interface CreditingVM {
  creditedPct: number;
  fossilNoResponsePct: number;
  crossBorderDeclinedPct: number;
  crossBorderCreditPct: number;
  floorPct: number;
  domestic: boolean;
  /** Grid/BA code for the copy, e.g. "MISO". */
  gridLabel?: string;
  /** "project" | "portfolio" — tunes the copy for a group of projects. */
  scope?: "project" | "portfolio";
  /** Top displaced fossil fuels, for the FRF step ("71% gas, 27% coal"). */
  fuelMix?: { label: string; pct: number }[];
  /** Measured fossil-response spread from the model, when available. */
  frf?: {
    median?: number | null;
    p10?: number | null;
    p90?: number | null;
    method?: string | null;
  } | null;
  /** Measured per-neighbour δ, most-material first. */
  neighbors?: CreditNeighborVM[];
  /** Where a user goes for the underlying data. */
  supportingNote?: string;
}

export interface EndpointVM {
  cls: "h" | "b" | "e";
  label: string;
  valueText: string;
  sub: string;
  chipLabel?: string;
  chipValue?: string;
  benefit: boolean;
  whisker?: WhiskerVM;
}

export interface FuelSegVM {
  label: string;
  pct: number;
  gwhText: string;
  color: string;
}

export interface SummaryHeroVM {
  methodNote: string;
  endpoints: EndpointVM[];
  generationText: string;
  cfText?: string;
  displacedText?: string;
  co2Text?: string;
  displacedRangeNote?: string;
  fuels: FuelSegVM[];
  crediting?: CreditingVM;
  /** Opens the generation-profile explore overlay from the driver anchor. */
  onExploreGeneration?: () => void;
}

const pctText = (v: number) => (v < 1 ? "<1%" : `${Math.round(v)}%`);
const deltaPct = (d: number) => `${Math.round(Math.max(0, Math.min(1, d)) * 100)}%`;
const cap = (s: string) => s.charAt(0).toUpperCase() + s.slice(1);
const methodLabel = (m?: string | null) =>
  m === "pinn"
    ? "physics-informed marginal model"
    : m === "statistical"
      ? "statistical marginal model"
      : "the marginal model";

/** "71% gas and 27% coal" from the top displaced fuels. */
function fuelMixPhrase(mix?: { label: string; pct: number }[]): string | null {
  if (!mix || !mix.length) return null;
  return mix
    .slice(0, 2)
    .map((f) => `${Math.round(f.pct)}% ${f.label.toLowerCase()}`)
    .join(" and ");
}

/**
 * CreditingPanel — the single, plain-language explanation of how this
 * project's (or portfolio's) credit is figured. One expandable ladder:
 * the simple claim → what fossil actually backs down (with the measured
 * fossil-response spread) → cross-border (what runs through neighbours and
 * what we can confirm) → what we count. No scenario codes, no FRF/δ/β jargon.
 */
function CreditingPanel({ c }: { c: CreditingVM }) {
  const [open, setOpen] = useState(false);
  const subject = c.scope === "portfolio" ? "these projects" : "this project";
  const grid = c.gridLabel ? `${c.gridLabel}` : "this grid";
  const mixPhrase = fuelMixPhrase(c.fuelMix);
  const hasFrf = !!c.frf && (c.frf.median != null || c.frf.p10 != null);
  const neighbors = (c.neighbors ?? []).filter((n) => (n.gwh ?? 0) > 0);

  return (
    <div className={`crediting ${open ? "open" : ""}`}>
      <button
        type="button"
        className="cr-toggle"
        aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
      >
        <span className="cr-lead">
          We count <b>~{pctText(c.creditedPct)}</b> of a simple “one-for-one”
          claim
        </span>
        <span className="cr-hint">Why — measured for {subject} in {grid}</span>
        <span className="chev">▾</span>
      </button>

      <div className="cr-drill">
        <div className="cr-sub">
          A simple estimate assumes every unit of clean energy replaces a unit
          of fossil energy, one-for-one. We don’t assume that — we measure, for{" "}
          {subject}’s own hours, how much fossil actually backs down and how much
          of the effect its neighbours carry.
        </div>

        {/* one honest stacked bar: counted (local + via neighbours) vs. the two
            things we can't / don't credit — the four segments sum to 100 */}
        <div className="cr-bar" role="img" aria-label="crediting breakdown">
          <span
            className="seg-credit"
            style={{ width: `${c.floorPct}%` }}
            title={`Counted, local: ${pctText(c.floorPct)}`}
          />
          <span
            className="seg-credit-xb"
            style={{ width: `${c.crossBorderCreditPct}%` }}
            title={`Counted, via neighbours: ${pctText(c.crossBorderCreditPct)}`}
          />
          <span
            className="seg-fossil"
            style={{ width: `${c.fossilNoResponsePct}%` }}
            title={`Fossil doesn't back down: ${pctText(c.fossilNoResponsePct)}`}
          />
          <span
            className="seg-xb"
            style={{ width: `${c.crossBorderDeclinedPct}%` }}
            title={`Cross-border we can't confirm: ${pctText(c.crossBorderDeclinedPct)}`}
          />
        </div>
        <div className="cr-legend">
          <span><i className="k-credit" />counted locally</span>
          {c.crossBorderCreditPct >= 1 && (
            <span><i className="k-credit-xb" />counted via neighbours</span>
          )}
          {c.fossilNoResponsePct >= 1 && (
            <span><i className="k-fossil" />fossil doesn’t respond</span>
          )}
          {c.crossBorderDeclinedPct >= 1 && (
            <span><i className="k-xb" />can’t confirm</span>
          )}
        </div>

        <div className="cstep">
          <span className="cmk simple" />
          <div className="cb-t">
            <div className="ct">Start: the simple claim</div>
            <div className="cd">
              Assume every unit of clean energy {subject} makes replaces a unit
              of fossil energy, one-for-one.
            </div>
          </div>
          <span className="camt">100%</span>
        </div>

        <div className="cstep">
          <span className="cmk fossil" />
          <div className="cb-t">
            <div className="ct">− Fossil that doesn’t back down</div>
            <div className="cd">
              {mixPhrase ? (
                <>
                  {cap(subject)}’s output displaces {mixPhrase} on the margin — a
                  fossil-heavy grid, so nearly every clean MWh makes a fossil
                  unit reduce output.{" "}
                </>
              ) : (
                <>
                  We measure the share of {subject}’s output that actually makes
                  a fossil unit reduce output.{" "}
                </>
              )}
              {c.fossilNoResponsePct < 1
                ? "Almost none is lost to already-clean demand or curtailment here."
                : "The rest meets demand already served cleanly, or is curtailed."}
            </div>
            <div className="csrc">
              <span className="tag">MEASURED</span>{" "}
              {hasFrf ? (
                <>
                  {methodLabel(c.frf!.method)} — fossil-response{" "}
                  {c.frf!.median != null && (
                    <>median <b>{deltaPct(c.frf!.median)}</b></>
                  )}
                  {c.frf!.p10 != null && c.frf!.p90 != null && (
                    <> (most hours {deltaPct(c.frf!.p10)}–{deltaPct(c.frf!.p90)})</>
                  )}
                  , hour-by-hour
                </>
              ) : (
                <>hourly grid operations</>
              )}
            </div>
          </div>
          <span className="camt minus">−{pctText(c.fossilNoResponsePct)}</span>
        </div>

        <div className="cstep">
          <span className="cmk xb" />
          <div className="cb-t">
            <div className="ct">
              {c.domestic
                ? "Cross-border: negligible here"
                : "Cross-border: what neighbours carry"}
            </div>
            <div className="cd">
              {c.domestic ? (
                <>
                  Essentially all of {subject}’s effect stays inside {grid} —
                  almost none of it crosses a grid boundary, so there’s nothing
                  to confirm or discount here.
                </>
              ) : (
                <>
                  About <b>{pctText(c.crossBorderCreditPct)}</b> of the effect
                  runs through exchanges with neighbouring grids
                  {neighbors.length > 0 && (
                    <> ({neighbors.slice(0, 3).map((n) => n.code).join(", ")})</>
                  )}
                  . We credit the share the measured exchange data confirms is
                  carried, and set aside the {pctText(c.crossBorderDeclinedPct)}{" "}
                  we can’t. Credit none of it and {subject} falls to the{" "}
                  {pctText(c.floorPct)} floor.
                </>
              )}
            </div>
            {!c.domestic && neighbors.length > 0 && (
              <div className="csrc">
                <span className="tag">MEASURED</span> neighbour carry-through:{" "}
                {neighbors.slice(0, 3).map((n, i) => (
                  <span key={n.code}>
                    {i > 0 && ", "}
                    {n.code} <b>{deltaPct(n.delta)}</b>
                    {n.kind !== "measured" && <span className="est"> est.</span>}
                  </span>
                ))}
              </div>
            )}
          </div>
          <span className="camt minus">−{pctText(c.crossBorderDeclinedPct)}</span>
        </div>

        <div className="cstep credit">
          <span className="cmk cr" />
          <div className="cb-t">
            <div className="ct">= What we count</div>
            <div className="cd">
              Our central estimate credits {subject} with{" "}
              {pctText(c.creditedPct)} of the simple claim. Each impact’s range
              bar above spans the most-cautious reading (floor,{" "}
              {pctText(c.floorPct)}) up to the simple claim (ceiling, 100%); the
              dot is this central estimate.
            </div>
          </div>
          <span className="camt cr">{pctText(c.creditedPct)}</span>
        </div>

        {c.supportingNote && (
          <div className="cr-foot">
            <span className="k">Supporting data</span>
            {c.supportingNote}
          </div>
        )}
      </div>
    </div>
  );
}

function Whisker({ w, benefit }: { w: WhiskerVM; benefit: boolean }) {
  const pct = Math.max(0, Math.min(100, w.centralPct));
  return (
    <div className="whisk">
      <div className="trk">
        <div className="fl" style={{ left: 0, width: `${pct}%` }} />
        <span className="cap" style={{ left: 0 }} />
        <span className="cap" style={{ right: 0 }} />
        <span className="cn" style={{ left: `${pct}%` }} />
      </div>
      <div className="sc">
        <div>
          <span className="t">{benefit ? "Floor" : "Ceiling"}</span>
          {w.floorText}
        </div>
        <div className="hi">
          <span className="t">{benefit ? "Ceiling" : "Floor"}</span>
          {w.ceilingText}
        </div>
      </div>
    </div>
  );
}

export function SummaryHero({ vm }: { vm: SummaryHeroVM }) {
  return (
    <div className="card">
      <div className="ch">
        <h2>Impact summary</h2>
        <span className="mut">{vm.methodNote}</span>
      </div>
      <div className="cb">
        <div className="grid2">
          <div className="grid3">
            {vm.endpoints.map((ep) => (
              <div
                key={ep.cls}
                className={`ep ${ep.cls} ${ep.benefit ? "pos" : "neg"}`}
              >
                <div className="lab">
                  <span className="dot" />
                  {ep.label}
                </div>
                <div className="big">{ep.valueText}</div>
                <div className="sub">{ep.sub}</div>
                {ep.chipValue && (
                  <div className="base">
                    <span className="mut">{ep.chipLabel}</span>
                    <span className="chip">{ep.chipValue}</span>
                  </div>
                )}
                {ep.whisker && (
                  <Whisker w={ep.whisker} benefit={ep.benefit} />
                )}
              </div>
            ))}
          </div>

          <div>
            <div className="anchor">
              <div className="lk">
                <svg
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth={2}
                >
                  <rect x="5" y="11" width="14" height="9" rx="2" />
                  <path d="M8 11V8a4 4 0 0 1 8 0v3" />
                </svg>
              </div>
              <div>
                <div className="l">Generation — the driver</div>
                <div className="v">{vm.generationText}</div>
                {vm.onExploreGeneration && (
                  <button
                    type="button"
                    className="gen-explore"
                    onClick={vm.onExploreGeneration}
                  >
                    View generation profile →
                  </button>
                )}
              </div>
              <div className="fx">
                {vm.cfText}
                <br />
                <b>fixed</b> across scenarios
              </div>
            </div>

            {vm.displacedText ? (
              <div className="disp">
                <div className="hl">
                  <b>{vm.displacedText}</b> fossil displaced
                  {vm.co2Text && (
                    <>
                      {" · "}
                      <b>{vm.co2Text}</b> CO₂e avoided
                    </>
                  )}
                </div>
                {vm.displacedRangeNote && (
                  <div className="mut">{vm.displacedRangeNote}</div>
                )}
                {vm.fuels.length > 0 && (
                  <>
                    <div className="fuelbar">
                      {vm.fuels.map((f) => (
                        <span
                          key={f.label}
                          style={{ width: `${f.pct}%`, background: f.color }}
                          title={f.label}
                        />
                      ))}
                    </div>
                    <div className="flegend">
                      {vm.fuels.map((f) => (
                        <span key={f.label}>
                          <i style={{ background: f.color }} />
                          {f.label} {Math.round(f.pct)}%
                        </span>
                      ))}
                    </div>
                  </>
                )}
              </div>
            ) : (
              <div className="disp">
                <div className="mut">
                  No fossil displacement recorded for this run.
                </div>
              </div>
            )}
          </div>
        </div>

        {vm.crediting && <CreditingPanel c={vm.crediting} />}
      </div>
    </div>
  );
}
